import { Loader, Title, Text, Button } from "dash-styled"
import { turbine } from "neql"
import React, { FC, useCallback, useMemo } from "react"
import { useAppInstance } from "../../../../../../Hooks/useAppInstance"
import { useAppMeasurerMetrics } from "../../../../../../Hooks/useAppMeasurerMetrics"
import { useAssumedRoles } from "../../../../../../Hooks/useAssumedRoles"
import { useBoolean } from "../../../../../../Hooks/useBoolean"
import { useGroundTruthList } from "../../../../../../Hooks/useGroundTruthList"
import { useNeqlContextProvider } from "../../../../../../Hooks/useNeqlContextProvider"
import { useQualifierTestCreate } from "../../../../../../Hooks/useQualifierTestCreate"
import { useQualifierTutorialCreate } from "../../../../../../Hooks/useQualifierTutorialCreate"
import { useTestQualifiedWorkers } from "../../../../../../Hooks/useTestQualifiedWorkers"
import { useUserPlan } from "../../../../../../Hooks/useUserPlan"
import { UserPlan } from "../../../../../../Models/API/UserModel"
import { getValue } from "../../../../../../Models/Client/FetchState"
import { hasAppPermission } from "../../../../../../Models/Client/ProjectRole"
import {
    AppDetailBase,
    AppDetailBaseProps
} from "../AppDetailBase/AppDetailBase"
import { CannotCreateQualifierModal } from "./Common/CannotCreateQualifierModal"
import { QualifierCard } from "./Common/LabelerQualification/QualifierCard"
import { WhosQualifiedTable } from "./Common/LabelerQualification/WhosQualifiedTable"

interface QualifiersProps {
    orgUsername: string
    appID: string
}

// The current implementation qualifiers is based on the assumption that only one each of TEST and TUTORIAL
// will be added. If and when multiple of the TEST or TUTORIAL is allowed,
// need to update the implementation to consider customising the training before creating.
// Since, the current logic would show only one TEST or TUTORIAL when added newly before actually created, ignoring the list from remote api
export const Qualifiers: FC<QualifiersProps> = ({ orgUsername, appID }) => {
    const [
        freemiumUserContact,
        showFreemiumUserContact,
        hideFreemiumUserContact
    ] = useBoolean(false)
    const userPlan = useUserPlan()

    const clientContext = useNeqlContextProvider("turbine")
    const instanceFetch = useAppInstance(appID)
    const rolesFetch = useAssumedRoles(appID)
    const appRoles = getValue(rolesFetch) ?? []
    const metrics = getValue(useAppMeasurerMetrics(appID))

    const fetchTests = turbine.useFetchTrainingTestsQuery({
        variables: {
            trainingTestsAppUuid: appID
        },
        client: clientContext
    })

    const fetchTestsResult = useMemo(
        () => fetchTests.data?.trainingTests ?? [],
        [fetchTests.data]
    )

    const fetchTutorials = turbine.useFetchTrainingTutorialsQuery({
        variables: {
            trainingTutorialsAppUuid: appID
        },
        client: clientContext
    })

    const fetchTutorialsResult = useMemo(
        () => fetchTutorials.data?.trainingTutorials ?? [],
        [fetchTutorials.data]
    )

    const [cannotCreateModal, showCannotCreateModal, hideCannotCreateModal] =
        useBoolean(false)

    const baselineData = useGroundTruthList(appID, {
        filter: [],
        sort: { sortBy: "used", desc: true }
    })
    const totalBaseline = useMemo(
        () => getValue(baselineData)?.total ?? 0,
        [baselineData]
    )
    const appInstance = getValue(instanceFetch)

    const { sampleTest, addSampleTest, createOrUpdateTest, isUpdatingTest } =
        useQualifierTestCreate(
            appID,
            appInstance?.name,
            fetchTests.refetch,
            totalBaseline
        )

    const {
        sampleTutorial,
        addSampleTutorial,
        createOrUpdateTutorial,
        isUpdatingTutorial
    } = useQualifierTutorialCreate(
        appID,
        appInstance?.name,
        fetchTutorials.refetch
    )

    const tutorials = useMemo(
        () =>
            sampleTutorial
                ? [sampleTutorial]
                : fetchTutorialsResult.reduce<turbine.TrainingTutorial[]>(
                      (acc, curr) =>
                          curr !== undefined && curr !== null
                              ? [...acc, curr]
                              : acc,
                      []
                  ),
        [fetchTutorialsResult, sampleTutorial]
    )

    const tests = useMemo(
        () =>
            sampleTest
                ? [sampleTest]
                : fetchTestsResult.reduce<turbine.TrainingTest[]>(
                      (acc, curr) =>
                          curr !== undefined && curr !== null
                              ? [...acc, curr]
                              : acc,
                      []
                  ),
        [fetchTestsResult, sampleTest]
    )

    const [
        qualifiedWorkers,
        canFetchMoreQualifiedWorkers,
        loadMoreQualifiedWorkers,
        refetchQualifiedWorkers
    ] = useTestQualifiedWorkers(
        tests[0]?.trainingId,
        hasAppPermission(appRoles, "whosqualified:view")
    )

    const showAddTestButton = tests.length === 0 && (metrics?.length ?? 0) > 0

    const showAddTutorialButton =
        tutorials.length === 0 && (metrics?.length ?? 0) > 0

    const addNewTest = useCallback(async () => {
        if (userPlan === UserPlan.FREEMIUM) {
            showFreemiumUserContact()
            return
        }
        if (totalBaseline > 0) {
            addSampleTest()
        } else if (totalBaseline === 0) {
            showCannotCreateModal()
        }
    }, [
        addSampleTest,
        showCannotCreateModal,
        showFreemiumUserContact,
        totalBaseline,
        userPlan
    ])

    const addNewTutorial = useCallback(async () => {
        if (userPlan === UserPlan.FREEMIUM) {
            showFreemiumUserContact()
            return
        }
        if (totalBaseline > 0) {
            addSampleTutorial()
        } else if (totalBaseline === 0) {
            showCannotCreateModal()
        }
    }, [
        addSampleTutorial,
        showCannotCreateModal,
        showFreemiumUserContact,
        totalBaseline,
        userPlan
    ])

    const baseProps: AppDetailBaseProps = {
        orgUsername,
        id: appID,
        title: "Worker qualification",
        breadcrumbs: ["Workers", "Worker qualification"],
        fixedContentWidth: false,
        forceUserExpiryContact: freemiumUserContact,
        resetUserExpiryContact: hideFreemiumUserContact
    }

    const renderQualifierCards = (type: turbine.TrainingType) => {
        switch (type) {
            case turbine.TrainingType.Tutorial:
                return (
                    <div className="flex flex-col space-y-4">
                        {tutorials.map((tutorial: turbine.TrainingTutorial) => (
                            <QualifierCard
                                key={tutorial.id}
                                orgUsername={orgUsername}
                                appID={appID}
                                type={type}
                                qualifier={tutorial}
                                reload={fetchTutorials.refetch}
                                totalBaseline={totalBaseline}
                                testId="qualifiers-tutorial"
                                createOrUpdateTest={createOrUpdateTest}
                                isLoadingTest={isUpdatingTest}
                                createOrUpdateTutorial={createOrUpdateTutorial}
                                isLoadingTutorial={isUpdatingTutorial}
                            ></QualifierCard>
                        ))}
                    </div>
                )
            case turbine.TrainingType.Test:
                return (
                    <div className="flex flex-col space-y-4">
                        {tests.map((test: turbine.TrainingTest) => (
                            <QualifierCard
                                key={test.id}
                                orgUsername={orgUsername}
                                appID={appID}
                                type={type}
                                qualifier={test}
                                reload={fetchTests.refetch}
                                reloadWorkers={refetchQualifiedWorkers}
                                totalBaseline={totalBaseline}
                                testId="qualifiers-tutorial"
                                createOrUpdateTest={createOrUpdateTest}
                                isLoadingTest={isUpdatingTest}
                                createOrUpdateTutorial={createOrUpdateTutorial}
                                isLoadingTutorial={isUpdatingTutorial}
                                maxQuestions={totalBaseline}
                            ></QualifierCard>
                        ))}
                    </div>
                )
        }
    }

    return (
        <AppDetailBase {...baseProps}>
            {fetchTests.loading && fetchTutorials.loading ? (
                <Loader />
            ) : (
                <div className="space-y-6 pb-16">
                    {
                        <div>
                            <div className="flex mb-4">
                                <div className="flex-grow">
                                    <Title
                                        variant="xl"
                                        weight="semibold"
                                        component="h3"
                                        className="text-rhino-900 mb-0"
                                    >
                                        Tutorials
                                    </Title>
                                    <Text
                                        className="text-smoke-810 my-0"
                                        size="sm"
                                    >
                                        Help workers learn by explaining the
                                        task and walking them through examples.
                                        Tutorials are not scored.
                                    </Text>
                                </div>
                                {showAddTutorialButton && (
                                    <>
                                        {isUpdatingTutorial ? (
                                            <div className="w-20">
                                                <Loader size="xxxl" />
                                            </div>
                                        ) : (
                                            <Button
                                                className="w-max-content h-8 rounded-lg text-sm py-1.5 px-2.5 flex-shrink-0 text-smoke-610"
                                                color="white"
                                                onClick={addNewTutorial}
                                            >
                                                Add tutorial
                                            </Button>
                                        )}
                                    </>
                                )}
                            </div>
                            {renderQualifierCards(
                                turbine.TrainingType.Tutorial
                            )}
                        </div>
                    }
                    <div>
                        <div className="flex mb-4">
                            <div className="flex-grow">
                                <Title
                                    variant="xl"
                                    weight="semibold"
                                    component="h3"
                                    className="text-rhino-900 mb-0"
                                >
                                    Tests
                                </Title>
                                <Text className="text-smoke-810 my-0" size="sm">
                                    Workers must reach a target score before
                                    they are allowed to process your data.
                                </Text>
                            </div>
                            {showAddTestButton && (
                                <>
                                    {isUpdatingTest ? (
                                        <div className="w-20">
                                            <Loader size="xxxl" />
                                        </div>
                                    ) : (
                                        <Button
                                            className="w-max-content h-8 rounded-lg text-sm py-1.5 px-2.5 flex-shrink-0 text-smoke-610"
                                            color="white"
                                            onClick={addNewTest}
                                        >
                                            Add test
                                        </Button>
                                    )}
                                </>
                            )}
                        </div>
                        {renderQualifierCards(turbine.TrainingType.Test)}
                    </div>

                    {tests.length > 0 &&
                        hasAppPermission(appRoles, "whosqualified:view") && (
                            <WhosQualifiedTable
                                testQualifier={tests[0]}
                                workersData={qualifiedWorkers}
                                reload={refetchQualifiedWorkers}
                                loadMore={
                                    canFetchMoreQualifiedWorkers
                                        ? loadMoreQualifiedWorkers
                                        : undefined
                                }
                            />
                        )}
                </div>
            )}
            {cannotCreateModal && (
                <CannotCreateQualifierModal onClose={hideCannotCreateModal} />
            )}
        </AppDetailBase>
    )
}
